import React, { createContext, useContext, useState, useCallback } from "react";
import { useSubject } from "./SubjectContext";
import { useApi } from "./ApiClient";
import { useAuth } from "react-oidc-context";
import { getCenterType } from "../utils/utils";
import { useConsortiaCenter } from "./ConsortiaCenterContext";
import { useCenter } from "./CenterContext";
import { normalizeVisitArrangementStatus } from "../utils/normalizeVisitArrangementStatus";
const DashboardContext = createContext(null);


export const DashboardProvider = ({ children }) => {
  const { centerNumber } = useCenter();
const { consortiaCentres =[] } = useConsortiaCenter()
const {subjectData} = useSubject();
const {selectedSubject, selectedSubjectId} = subjectData || {};

   const currentCentreType = getCenterType(consortiaCentres, Number(centerNumber), selectedSubject, selectedSubjectId) || '';
   

            
  const [dashboardCache, setDashboardCache] = useState({});
  const [loadingDashboard, setLoadingDashboard] = useState(false);
  const [dashboardError, setDashboardError] = useState(null);
  const { setSubjectData } = useSubject();
  const auth = useAuth();
  const api = useApi(process.env.REACT_APP_VAF_BASE_URL);
 
  const updateSubjectState = useCallback(
    (subject, subjectDetailsWithReports) => {
   setSubjectData(prev => ({
  ...prev,
  selectedSubjectId: subject.subjectId,
  selectedSubject: subject.subjectName,
  selectedSubjectCode: subject.certificateCodes?.join("/") || "",
  subjectDetails: subjectDetailsWithReports || null,

  // Add isRegistered ONLY when centre type is null/empty
  ...((!currentCentreType || currentCentreType.trim() === "") && {
    isRegistered: subjectDetailsWithReports?.isCandidateRegistered,
  }),

  // When centre type is Lead → add 3 keys
  ...(currentCentreType === "Lead" && {
    isConsortiaLeadCandidateRegistered:
      subjectDetailsWithReports?.isConsortiaLeadCandidateRegistered,
    isConsortiaRestCandidateRegistered:
      subjectDetailsWithReports?.isConsortiaRestCandidateRegistered,
    isLeadCandidateRegistered:
      subjectDetailsWithReports?.isLeadCandidateRegistered,
  }),

  // When centre type is Rest → add 2 keys
  ...(currentCentreType === "Rest" && {
    isConsortiaRestCandidateRegistered:
      subjectDetailsWithReports?.isConsortiaRestCandidateRegistered,
    isRestCandidateRegistered:
      subjectDetailsWithReports?.isRestCandidateRegistered,
  }),
}));

    },
    [setSubjectData]
  );
 
  const fetchDashboardDetails = useCallback(
  async (subject, centreNumber, options = {}) => {
    
    
    if (!subject?.subjectId) throw new Error("Missing subjectId");
    if (!centreNumber) throw new Error("Missing centreNumber");

    const { forceRefresh = false, skipCache = false } = options;
    const cacheKey = `${centreNumber}-${subject.subjectId}`;
    const cacheHit = dashboardCache[cacheKey];
const currentCentreType = getCenterType( consortiaCentres, Number(centreNumber), subject.subjectName, subject.subjectId ) || '';
    if (cacheHit && !forceRefresh) {
    
      updateSubjectState(subject, cacheHit.subjectDetails); // subjectDetails includes reportsList now
      return cacheHit;
    }

    setLoadingDashboard(true);
    setDashboardError(null);

    // Check for token before making API call
    const token = auth.user?.access_token;
    if (!token) {
      console.error("[Dashboard] Token is missing, cannot make the request.");
      setDashboardError(new Error("Missing authentication token."));
      setLoadingDashboard(false);
      return null;
    }
    try {
      const url = !currentCentreType ? `getDashboardDetails/${centreNumber}/${subject.subjectId}` : `getConsortiaDashboardDetails/${centreNumber}/${subject.subjectId}/${currentCentreType}`

      const response = await api.get(url, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      const parsedDataArr = normalizeVisitArrangementStatus(response?.data?.data);
      let payload = parsedDataArr?.[0] || null;

      const visits = payload?.visitArrangementResponse || [];
     

      const reportsList = visits
        .filter(
          (v) =>
            v.visitDate &&
            v.additionalVisitDate &&
            v.stageId &&
            typeof v.reportPath === "string" &&
            v.reportPath.trim() !== ""
        )
        .map((v) => {
          const units = (v.assessments || []).map((a) => a.split(":")[0]);
          const visit = new Date(v.visitDate);
        
          const dateToken = isNaN(visit)
            ? ""
            : visit.toISOString().slice(2, 10).replace(/-/g, ""); // YYMMDD
          const fileName = `${v.centreNumber}_${subject.subjectName.replace(
            /[^A-Za-z0-9]+/g,
            ""
          )}_ModerationReport_${dateToken}.pdf`;
          return {
            visitArrangementId: v.visitArrangementId,
            visitDate: v.visitDate,
            additionalVisitDate: v.additionalVisitDate,
            units,
            window: `${v.sessionMonth} ${v.sessionYear}`,
            visitType: v.moderationType === "Face-to-Face" ? "Face-to-face" : v.moderationType,
            fileName,
            reportUrl: v.reportPath,
            raw: v,
          };
        })
        .sort((a, b) => new Date(b.visitDate) - new Date(a.visitDate));
      const subjectDetailsWithReports = {
        ...payload,
        reportsList,
      };
      const normalized = {
        subjectId: subject.subjectId,
        subjectDetails: subjectDetailsWithReports, // embed reportsList
        reportsList,
        isRegistered: !currentCentreType  ?  payload?.isCandidateRegistered : currentCentreType === 'Lead' ? payload?.isLeadCandidateRegistered : payload?.isRestCandidateRegistered,
        isRestCandidateRegistered: subjectDetailsWithReports?.isRestCandidateRegistered ,

        fetchedAt: Date.now(),
      };

      updateSubjectState(subject, subjectDetailsWithReports);

      if (!skipCache) {
        setDashboardCache((prev) => ({
          ...prev,
          [cacheKey]: normalized,
        }));
      }
      return normalized;


    } catch (err) {
      setDashboardError(err);
      console.error("[Dashboard] fetch error:", err);
      updateSubjectState(subject, null);
      return null;
    } finally {
      setLoadingDashboard(false);
    }
  },
  [dashboardCache, updateSubjectState, auth.user?.access_token]
);


  const invalidateDashboard = useCallback((centreNumber, subjectId) => {
    const key = `${centreNumber}-${subjectId}`;
    setDashboardCache((prev) => {
      const copy = { ...prev };
      delete copy[key];
      return copy;
    });
   
  }, []);
 
  const resetDashboardCache = useCallback(() => {
    setDashboardCache({});
   
  }, []);
 
  return (
    <DashboardContext.Provider
      value={{
        fetchDashboardDetails,
        dashboardCache,
        loadingDashboard,
        dashboardError,
        invalidateDashboard,
        resetDashboardCache,
      }}
    >
      {children}
    </DashboardContext.Provider>
  );
};
 
export const useDashboard = () => {
  const ctx = useContext(DashboardContext);
  if (!ctx)
    throw new Error("useDashboard must be used within DashboardProvider");
  return ctx;
};
 
 